import React, { useEffect, useMemo, useState } from "react";
import { Building2, Search, Globe } from "lucide-react";
import Swal from "sweetalert2";
import { listRegionalEntities } from "../../services/regionalEntitiesService";
import EntityDetailsModal from "./Articulacion/Details/EntityDetailsModal";

/**
 * Props:
 * - refreshKey: number -> fuerza recarga desde el padre
 * - embedded: boolean -> si true, oculta sus propios filtros y usa search/region externos
 * - search: string -> búsqueda externa cuando embedded=true
 * - region: string -> filtro región externo cuando embedded=true
 */
export default function RegionalEntities({
  refreshKey = 0,
  embedded = false,
  search = "",
  region = "",
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // filtros internos (solo cuando !embedded)
  const [q, setQ] = useState("");
  const [rgn, setRgn] = useState("");

  const [entities, setEntities] = useState([]);
  const [openDetails, setOpenDetails] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  async function load({ search, region }) {
    try {
      setLoading(true);
      setError("");
      const res = await listRegionalEntities({
        page: 1,
        limit: 500,
        search: search || "",
        region: region || "",
      });
      const data = Array.isArray(res?.data) ? res.data : [];
      setEntities(data);
    } catch (e) {
      console.error(e);
      setError(e?.message || "No se pudo cargar el listado");
    } finally {
      setLoading(false);
    }
  }

  // carga inicial + cambios de filtros
  useEffect(() => {
    const s = embedded ? search : q;
    const rg = embedded ? region : rgn;
    load({ search: s, region: rg });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, rgn, refreshKey, embedded, search, region]);

  const regionOptions = useMemo(() => {
    const set = new Set(
      entities.map(e => (e.region || "").trim()).filter(Boolean)
    );
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [entities]);

  const openEntity = (id) => {
    setSelectedId(id);
    setOpenDetails(true);
  };

  const Card = ({ e }) => (
    <button
      type="button"
      onClick={() => openEntity(e.id)}
      className="group text-left rounded-2xl border border-gray-200 bg-white p-5 shadow-sm hover:shadow-md transition-all focus:outline-none"
    >
      <div className="flex items-center gap-4">
        {e.imageUrl ? (
          <img
            src={e.imageUrl}
            alt={e.name}
            className="w-12 h-12 rounded-xl object-contain bg-white ring-1 ring-gray-200 p-1"
          />
        ) : (
          <div className="w-12 h-12 rounded-xl bg-blue-50 border border-blue-100 grid place-content-center">
            <Building2 className="w-6 h-6 text-[#0488AF]" />
          </div>
        )}

        <div className="min-w-0">
          <div className="font-semibold text-gray-900 truncate">{e.name}</div>
          <div className="mt-1 text-xs text-gray-600">
            <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5">
              <Globe size={12} className="opacity-70" />
              {e.region || "Sin región"}
            </span>
          </div>
        </div>

        <span className="ml-auto shrink-0 rounded-full bg-gray-100 px-3 py-1 text-sm text-gray-700">
          {e.workersCount || 0} trabajador{(e.workersCount || 0) !== 1 ? "es" : ""}
        </span>
      </div>
    </button>
  );

  return (
    <div>
      {/* Filtros propios SOLO si no está embebido */}
      {!embedded && (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-4 items-end">
          {/* Buscar entidad */}
          <div className="lg:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Buscar entidad
            </label>
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Nombre de la entidad…"
                className="w-full pl-11 pr-4 py-3 rounded-2xl border border-gray-200 focus:ring-2 focus:ring-[#0488AF] outline-none"
              />
            </div>
          </div>

          {/* Región */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Filtrar por región
            </label>
            <select
              value={rgn}
              onChange={(e) => setRgn(e.target.value)}
              className="w-full px-3 py-3 rounded-2xl border border-gray-200 bg-white focus:ring-2 focus:ring-[#0488AF] outline-none"
            >
              <option value="">Todas las regiones</option>
              {regionOptions.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
        </div>
      )}

      {error && (
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 text-red-700 px-4 py-3">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-center text-gray-500 py-12">Cargando…</div>
      ) : entities.length === 0 ? (
        <div className="text-center text-gray-500 py-12">No hay entidades</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {entities.map(e => <Card key={e.id} e={e} />)}
        </div>
      )}

      {/* Detalle */}
      <EntityDetailsModal
        open={openDetails}
        entityId={selectedId}
        onClose={() => setOpenDetails(false)}
        onChanged={() => load({ search: embedded ? search : q, region: embedded ? region : rgn })}
      />
    </div>
  );
}
